import { memo, useMemo } from "react";
import { getWeaponProfile, weaponRings } from "@/engine/range.ts";
import {
  gameToScreen,
  metersToScreenRadius,
  type Camera,
  type ProjectionConfig,
} from "@/engine/projection.ts";
import type { Marker } from "@/session/schemas.ts";

interface RangeRingLayerProps {
  readonly markers: readonly Marker[];
  readonly camera: Camera;
  readonly config: ProjectionConfig;
}

/**
 * Range rings for placed artillery.
 *
 * Drawn from the weapon's own min/max figures (`engine/range.ts`), not a
 * hand-set radius, so a marker never quietly goes stale if those figures are
 * revised. The minimum ring matters as much as the maximum here — a mortar's
 * dead zone is exactly the kind of thing a commander finds out about the hard
 * way if it is not on the map.
 */
function RangeRingLayerImpl({
  markers,
  camera,
  config,
}: RangeRingLayerProps): React.ReactElement {
  const rings = useMemo(() => {
    const result: Array<{
      key: string;
      at: { x: number; y: number };
      radius: number;
      dashed: boolean;
      label: string;
      threat: boolean;
    }> = [];

    for (const marker of markers) {
      if (!marker.weaponId) continue;
      const weapon = getWeaponProfile(marker.weaponId);
      if (!weapon) continue;

      for (const ring of weaponRings(marker.at, weapon)) {
        result.push({
          key: `${marker.id}-${ring.kind}`,
          at: gameToScreen(marker.at, camera, config),
          radius: metersToScreenRadius(ring.radiusMeters, marker.at, camera, config),
          dashed: ring.kind === "min",
          label: ring.label,
          threat: Boolean(marker.threat),
        });
      }
    }

    return result;
  }, [markers, camera, config]);

  if (rings.length === 0)
    return <svg aria-hidden className="pointer-events-none absolute inset-0" />;

  return (
    <svg aria-hidden className="pointer-events-none absolute inset-0 h-full w-full">
      {rings.map((ring) => {
        const color = ring.threat ? "var(--color-danger)" : "var(--color-warning)";
        return (
          <circle
            key={ring.key}
            cx={ring.at.x}
            cy={ring.at.y}
            r={ring.radius}
            fill={ring.dashed ? "none" : color}
            fillOpacity={ring.dashed ? 0 : 0.05}
            stroke={color}
            strokeOpacity={0.55}
            strokeWidth={1.5}
            strokeDasharray={ring.dashed ? "5 4" : undefined}
          >
            <title>{ring.label}</title>
          </circle>
        );
      })}
    </svg>
  );
}

export const RangeRingLayer = memo(RangeRingLayerImpl);
